/**
 * Performance optimization utilities
 */

// Request idle callback polyfill
const requestIdleCallback = 
  window.requestIdleCallback || 
  ((cb: IdleRequestCallback) => setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 50 }), 1));

/**
 * Schedule non-critical work during browser idle time
 */
export const scheduleIdleWork = (callback: () => void, timeout = 2000) => {
  requestIdleCallback(callback, { timeout });
};

/**
 * Debounce function for performance-sensitive operations
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function for scroll/resize handlers
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Batch DOM reads/writes to avoid layout thrashing
 */
class DOMBatcher {
  private reads: (() => void)[] = [];
  private writes: (() => void)[] = [];
  private scheduled = false;

  read(fn: () => void) {
    this.reads.push(fn);
    this.schedule();
  }

  write(fn: () => void) {
    this.writes.push(fn);
    this.schedule();
  }

  private schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    
    requestAnimationFrame(() => {
      // Batch all reads first
      this.reads.forEach(fn => fn());
      this.reads = [];
      
      // Then batch all writes
      this.writes.forEach(fn => fn());
      this.writes = [];
      
      this.scheduled = false;
    });
  }
}

export const domBatcher = new DOMBatcher();

/**
 * Memory-efficient memoization with LRU cache
 */
export const memoizeWithLimit = <T extends (...args: any[]) => any>(
  fn: T,
  maxSize = 100
): T => {
  const cache = new Map<string, ReturnType<T>>();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(args);
    
    if (cache.has(key)) {
      const value = cache.get(key)!;
      // Move to end (most recently used)
      cache.delete(key);
      cache.set(key, value);
      return value;
    }
    
    const result = fn(...args);
    cache.set(key, result);
    
    // Remove oldest entry if over limit
    if (cache.size > maxSize) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    
    return result;
  }) as T;
};

/**
 * Preconnect to external domains for faster loading
 */
export const preconnect = (url: string) => {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = url;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

/**
 * DNS prefetch for domains we'll need later
 */
export const dnsPrefetch = (url: string) => {
  const link = document.createElement('link');
  link.rel = 'dns-prefetch';
  link.href = url;
  document.head.appendChild(link);
};

/**
 * Initialize performance optimizations
 */
export const initPerformanceOptimizations = () => {
  // Preconnect to Supabase
  preconnect('https://kllsxgvvwosxlcpxqwin.supabase.co');
  
  // Preconnect to Stripe
  preconnect('https://js.stripe.com');
  preconnect('https://api.stripe.com');
  
  // DNS prefetch for fonts
  dnsPrefetch('https://fonts.googleapis.com');
  dnsPrefetch('https://fonts.gstatic.com');
};

/**
 * Performance metrics tracking
 */
export const trackPerformanceMetric = (name: string, value: number) => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    performance.mark(`${name}-start`);
    
    // Report to analytics if available
    if (import.meta.env.PROD) {
      scheduleIdleWork(() => {
        console.log(`[Performance] ${name}: ${value.toFixed(2)}ms`);
      });
    }
  }
};
